import { useState } from "react";
import Accordion from "../components/Accordion";
import ButtonLink from "../components/ButtonLink";
import { DASHBOARD_SIGNUP_URL } from "../data/platform-links";
import { pricingPlans } from "../data/pricing-plans";
import "./PricingPage.css";

const trialQuestions = [
  {
    title: "What’s included in my 7-day trial?",
    body: [
      "Try web content, educator analytics, and student join codes for web and VR. VR setup is included. Custom guide creation is available with a paid Classroom plan or Site license.",
    ],
  },
  {
    title: "What happens after my trial?",
    body: [
      "You can still sign in to your account. Choose a plan to continue accessing content and the tools included with that plan.",
    ],
  },
  {
    title: "Which plans include VR?",
    body: [
      "The Site license includes unlimited VR access across your site, plus student join codes for web and VR. Individual and Classroom plans include web access. You can try VR during your 7-day trial.",
    ],
  },
  {
    title: "How do I manage renewal?",
    body: [
      "Paid plans renew automatically. Turn off auto-renew from your account at any time. Your access continues through the end of your paid term.",
    ],
  },
];

export default function PricingPage() {
  const [individualInterval, setIndividualInterval] = useState("year");

  return (
    <article className="pricing-page">
      <section className="pricing-overview" aria-labelledby="pricing-title">
        <div className="content-width">
          <header className="pricing-hero">
            <h1 className="eyebrow" id="pricing-title">Pricing</h1>
            <p className="pricing-hero__lead">
              Explore on your own, teach your classes, or bring science to your
              whole site. Start with a 7-day trial.
            </p>
          </header>

          <div className="pricing-plans" aria-label="Available plans">
            {pricingPlans.map((plan) => {
              const isIndividual = plan.id === "individual";
              const isClassroom = plan.id === "classroom";
              const interval = isIndividual ? individualInterval : "year";

              return (
                <section
                  className={`pricing-plan${isClassroom ? " pricing-plan--classroom" : ""}`}
                  aria-labelledby={`pricing-${plan.id}`}
                  key={plan.id}
                >
                  <div className="pricing-plan__intro">
                    <p className="pricing-plan__platform">{plan.platform}</p>
                    <h2 id={`pricing-${plan.id}`}>{plan.name}</h2>
                    <p className="pricing-plan__description">{plan.description}</p>
                  </div>

                  <div className="pricing-plan__billing">
                    <p className="pricing-plan__price" aria-live="polite" aria-atomic="true">
                      {interval === "month" ? plan.monthlyPrice : plan.annualPrice}
                      <span> / {interval}</span>
                    </p>
                    {isIndividual ? (
                      <fieldset className="pricing-interval">
                        <legend className="visually-hidden">Individual billing period</legend>
                        {[
                          ["year", "Annual"],
                          ["month", "Monthly"],
                        ].map(([value, label]) => (
                          <label key={value}>
                            <input
                              type="radio"
                              name="individual-billing-period"
                              value={value}
                              checked={individualInterval === value}
                              onChange={() => setIndividualInterval(value)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </fieldset>
                    ) : (
                      <p className="pricing-plan__term">Billed annually</p>
                    )}
                  </div>

                  <ButtonLink
                    href={DASHBOARD_SIGNUP_URL}
                    variant={isClassroom ? "dark" : "outline"}
                    className="pricing-plan__cta"
                  >
                    Start a 7-day trial
                  </ButtonLink>

                  <div className="pricing-plan__includes">
                    <h3>{plan.heading}</h3>
                    <ul aria-label={`${plan.name} features`}>
                      {plan.features.map((feature) => (
                        <li key={feature}>
                          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                            <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              );
            })}
          </div>
          <p className="pricing-overview__note">
            Prices in USD. Paid plans renew automatically. Manage renewal anytime
            from your account.
          </p>
        </div>
      </section>

      <section className="pricing-trial" aria-labelledby="pricing-trial-title">
        <div className="content-width pricing-trial__inner">
          <div className="pricing-trial__intro">
            <p className="eyebrow">Your next discovery starts here</p>
            <h2 id="pricing-trial-title">Give curiosity <span>7 days.</span></h2>
            <p>
              Explore the science. Try the teaching tools. See what 10k Science
              can bring to your classroom.
            </p>
            <ButtonLink href={DASHBOARD_SIGNUP_URL}>
              Start a 7-day trial
            </ButtonLink>
          </div>
          <div className="pricing-trial__questions">
            <Accordion items={trialQuestions} />
          </div>
        </div>
      </section>
    </article>
  );
}
